import React, { useEffect, useRef, useState } from 'react';
import {
  X, RefreshCw, Loader2, CheckCircle, Printer, AlertCircle, Lock, Calculator, LayoutDashboard, Users, Gift, Banknote, Wallet,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import StaffPanel from './StaffPanel';
import RegisterSummary from './RegisterSummary';
import StaffActionsTab, { StaffActionsView } from './StaffActionsTab';
import DepositsPanel from '../deposits/DepositsPanel';
import { useDepositsConfig } from '../deposits/useDepositsConfig';
import CashMovementsPanel from './cashMovements/CashMovementsPanel';
import { useCashMovementsConfig } from './cashMovements/useCashMovementsConfig';
import { registerAccess, RegisterAccess, RegisterTab, withDepositsTab, withMovementsTab } from './registerAccess';
import { useRegisterActions, useRegisterReport, OpenItem } from './useRegisterHub';
import {
  OPEN_PLATFORM_LABELS, OPEN_STATUS_LABELS, OPEN_TABLE_STATUS_LABELS, athensDateTime, euro,
} from './registerFormat';

/**
 * «Ταμείο» — ΕΝΑ παράθυρο από το μενού χρήστη στη θέση των «Πληρωμές Προσωπικού», «Χ Αναφορά»,
 * «Κεράσματα» και «Κλείσιμο Ταμείου» (απόφαση ιδιοκτήτη 6/10/2026). Φαρδύ, για τις οριζόντιες οθόνες
 * αφής των ταμείων: καρτέλες πάνω (Σύνοψη · Προσωπικό · Κεράσματα & ακυρώσεις), κάρτες δίπλα-δίπλα,
 * και οι ενέργειες ΠΑΝΤΑ ορατές στο κάτω μέρος (Επανεκτύπωση τελευταίου Ζ · Εκτύπωση Χ · Κλείσιμο
 * ταμείου, με επιβεβαίωση μέσα στο παράθυρο). Ποσά και εκτυπώσεις όπως πριν — αλλάζει μόνο η οθόνη.
 * Δικαιώματα: registerAccess (ίδιοι κανόνες με τις παλιές επιλογές του μενού).
 */

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const TAB_META: Record<RegisterTab, { label: string; icon: React.ElementType }> = {
  summary: { label: 'Σύνοψη', icon: LayoutDashboard },
  movements: { label: 'Κινήσεις ταμείου', icon: Wallet },
  staff: { label: 'Προσωπικό', icon: Users },
  actions: { label: 'Κεράσματα & ακυρώσεις', icon: Gift },
  deposits: { label: 'Προκαταβολές', icon: Banknote },
};

/**
 * Μέγιστο μέγεθος ~1200×860 σε pixel της ΟΘΟΝΗΣ, αλλιώς 96%×94% της. Σε ταμεία μικρότερα από 1366×768 η
 * εφαρμογή τρέχει με CSS zoom (public/index.html): τα 1200×860 «CSS px» θα έβγαιναν μικρότερα στην
 * οθόνη (900×645 σε 1024×768), γι' αυτό διαιρούνται με το zoom. Στο κινητό: όλη η οθόνη.
 */
const dialogCaps = (): React.CSSProperties | undefined => {
  if (typeof window === 'undefined' || window.innerWidth < 640) return undefined;
  const zoom = Number(document.documentElement.style.getPropertyValue('zoom')) || 1;
  return { maxWidth: `${Math.round(1200 / zoom)}px`, maxHeight: `${Math.round(860 / zoom)}px` };
};

const secondaryBtn = 'min-h-[48px] px-4 rounded-xl border border-line-strong text-t2 hover:border-slate-400 hover:text-t1 bg-surface-1 text-sm font-semibold flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation';
const primaryBtn = (tone: string) => `min-h-[48px] px-5 rounded-xl ${tone} text-white font-semibold flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:bg-surface-3 disabled:text-t3 disabled:cursor-not-allowed touch-manipulation`;

/** Τα ανοιχτά τραπέζια / οι παραγγελίες σε εξέλιξη που μπλόκαραν το κλείσιμο (409). */
const OpenItemsList: React.FC<{ items: OpenItem[] }> = ({ items }) => {
  // Ο server στέλνει ένα από τα δύο κάθε φορά, πρώτα τα τραπέζια.
  const tables = items.every(o => o.order_type === 'dine_in');
  return (
    <div data-testid="register-open-items" className="mt-2">
      <div className="max-h-40 overflow-y-auto grid gap-1 sm:grid-cols-2 pr-1">
        {items.map(o => (
          <div key={o.id} className="flex items-center justify-between gap-2 text-xs text-t2 bg-surface-2 dark:bg-surface-2/50 rounded-lg px-2.5 py-1.5">
            <span className="font-medium">
              {o.code}
              {o.order_type !== 'dine_in' && (
                <span className="text-t4 ml-1.5">{OPEN_PLATFORM_LABELS[o.platform] || o.platform}</span>
              )}
            </span>
            <span className="flex items-center gap-2">
              <span className="text-amber-700/90 dark:text-amber-400/90">
                {o.order_type === 'dine_in'
                  ? (OPEN_TABLE_STATUS_LABELS[o.status] || 'Ανοιχτό')
                  : (OPEN_STATUS_LABELS[o.status] || o.status)}
              </span>
              <span className="text-t3 tabular-nums">{euro(o.total)}</span>
            </span>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-t4 pt-1">
        {tables
          ? 'Ολοκλήρωσε την πληρωμή ή ακύρωσέ τα από τα «Τραπέζια» και πάτησε ξανά «Κλείσιμο ταμείου».'
          : 'Ολοκλήρωσε ή ακύρωσέ τις από τις «Παραγγελίες» και πάτησε ξανά «Κλείσιμο ταμείου».'}
      </p>
    </div>
  );
};

const Warning: React.FC<{ testId?: string; title: string; children?: React.ReactNode }> = ({ testId, title, children }) => (
  <div data-testid={testId} role="alert" className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
    <div className="flex items-start gap-2 text-amber-800 dark:text-amber-300 text-sm font-semibold">
      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
      <span>{title}</span>
    </div>
    {children && <div className="pl-6">{children}</div>}
  </div>
);

const Success: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div role="status" className="flex items-center gap-2 text-sm font-semibold text-emerald-600 dark:text-emerald-400">
    <CheckCircle className="w-5 h-5 shrink-0" /> {children}
  </div>
);

const RegisterHub: React.FC<{ onClose: () => void; storeId: number; access: RegisterAccess }> = ({ onClose, storeId, access: baseAccess }) => {
  const { store, employee } = useAuth();
  // «Προκαταβολές»: μόνο με ανοιχτό τον διακόπτη του καταστήματος (πύλη και στον server).
  const deposits = useDepositsConfig(storeId);
  // «Κινήσεις ταμείου»: με ανοιχτό διακόπτη, ή όταν η περίοδος έχει ήδη κινήσεις (πύλη και στον server).
  const movements = useCashMovementsConfig(storeId);
  const { report, loading, error, reload } = useRegisterReport(storeId);
  const hasMovements = (report?.cashMovements?.items?.length ?? 0) > 0;
  const access = withMovementsTab(withDepositsTab(baseAccess, employee, deposits.enabled),
    !!movements.config && (movements.enabled || hasMovements));
  const [tab, setTab] = useState<RegisterTab>(access.tabs[0]);
  const activeTab = access.tabs.includes(tab) ? tab : access.tabs[0];
  const { busy, notice, closed, printX, closeRegister, reprintLast } = useRegisterActions(storeId, onClose);
  const [refreshKey, setRefreshKey] = useState(0);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [actionsView, setActionsView] = useState<StaffActionsView>('list');
  const [trainingCloseArmed, setTrainingCloseArmed] = useState(false);
  const trainingHoldTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const trainingHoldPointer = useRef<number | null>(null);
  const trainingHoldTriggered = useRef(false);
  const suppressCloseClick = useRef(false);
  const trainingCloseEnabled = report?.trainingModeEnabled === true;

  const clearTrainingHold = () => {
    if (trainingHoldTimer.current) clearTimeout(trainingHoldTimer.current);
    trainingHoldTimer.current = null;
    trainingHoldPointer.current = null;
    trainingHoldTriggered.current = false;
    setTrainingCloseArmed(false);
  };

  useEffect(() => () => {
    if (trainingHoldTimer.current) clearTimeout(trainingHoldTimer.current);
  }, []);

  const refresh = () => {
    reload();
    setRefreshKey(k => k + 1);
  };

  const waste = (report as { waste?: { entries: number } | null } | null)?.waste;
  const wasteMissing = !!waste && waste.entries === 0;

  const startClose = () => setConfirmingClose(true);
  const confirmClose = () => {
    setConfirmingClose(false);
    closeRegister();
  };

  const startTrainingHold = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!trainingCloseEnabled || busy !== null || loading || !report || !event.isPrimary || event.button !== 0) return;
    trainingHoldPointer.current = event.pointerId;
    trainingHoldTriggered.current = false;
    trainingHoldTimer.current = setTimeout(() => {
      trainingHoldTriggered.current = true;
      setTrainingCloseArmed(true);
    }, 2000);
  };

  const endTrainingHold = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (trainingHoldPointer.current !== event.pointerId) return;
    const shouldTrainingClose = trainingHoldTriggered.current;
    if (trainingHoldTimer.current) clearTimeout(trainingHoldTimer.current);
    trainingHoldTimer.current = null;
    trainingHoldPointer.current = null;
    trainingHoldTriggered.current = false;
    setTrainingCloseArmed(false);
    if (shouldTrainingClose) {
      suppressCloseClick.current = true;
      setConfirmingClose(false);
      closeRegister(true);
    }
  };

  const body = () => {
    if (activeTab === 'staff') {
      return <StaffPanel storeId={storeId} staffReport={report} refreshKey={refreshKey} />;
    }
    if (activeTab === 'deposits' && deposits.config) {
      return <DepositsPanel storeId={storeId} config={deposits.config} canCollect refreshKey={refreshKey} />;
    }
    if (error) {
      return (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-700 dark:text-red-300 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </div>
      );
    }
    if (!report) {
      return loading
        ? <div className="flex justify-center py-12"><RefreshCw className="w-7 h-7 text-blue-600 dark:text-blue-400 animate-spin" /></div>
        : null;
    }
    if (activeTab === 'movements' && movements.config) {
      return <CashMovementsPanel storeId={storeId} config={movements.config} report={report} onChanged={refresh} />;
    }
    if (activeTab === 'actions') {
      return (
        <StaffActionsTab
          storeId={storeId}
          actions={report.staffActions}
          view={actionsView}
          onViewChange={setActionsView}
          refreshKey={refreshKey}
        />
      );
    }
    return <RegisterSummary report={report} storeSections={store?.register_report_sections} closeAllowed={access.close} />;
  };

  // ── Κάτω μέρος: αποτέλεσμα της τελευταίας ενέργειας + κουμπιά ──────────────────────────────────
  const status = () => {
    if (closed) {
      if (!closed.printed) {
        return (
          <Warning testId="register-not-printed" title="Το ταμείο έκλεισε, αλλά η αναφορά ΔΕΝ εκτυπώθηκε.">
            <p className="text-xs text-t3 mt-1">
              Δεν βρέθηκε ενεργός εκτυπωτής αποδείξεων ή η εκτύπωση απέτυχε. Το κλείσιμο έχει καταγραφεί — μην το επαναλάβεις, τύπωσε αντίγραφο.
            </p>
            {notice?.kind === 'error' && (
              <p className="text-xs text-red-700 dark:text-red-300 mt-2">{notice.message}</p>
            )}
          </Warning>
        );
      }
      return (
        <div className="space-y-1">
          <Success>Το ταμείο έκλεισε & εκτυπώθηκε</Success>
          {notice?.kind === 'reprinted' && (
            <p className="text-xs text-emerald-600 dark:text-emerald-400 pl-7">{notice.message}</p>
          )}
        </div>
      );
    }
    if (!notice) return null;
    switch (notice.kind) {
      case 'x_printed':
        return <Success>Η Χ αναφορά εκτυπώθηκε</Success>;
      case 'x_not_printed':
        return (
          <Warning testId="register-x-not-printed" title="Η Χ αναφορά ΔΕΝ εκτυπώθηκε.">
            <p className="text-xs text-t3 mt-1">
              Δεν βρέθηκε ενεργός εκτυπωτής αποδείξεων ή η εκτύπωση απέτυχε. Έλεγξε τον εκτυπωτή και πάτησε ξανά «Εκτύπωση Χ».
            </p>
          </Warning>
        );
      case 'reprinted':
        return <Success>{notice.message}</Success>;
      default:
        return (
          <Warning testId="register-action-error" title={notice.message}>
            {notice.openOrders && notice.openOrders.length > 0 && <OpenItemsList items={notice.openOrders} />}
          </Warning>
        );
    }
  };

  const reprintButton = (emphasis: boolean) => (
    <button
      type="button"
      onClick={reprintLast}
      disabled={busy !== null}
      className={emphasis ? primaryBtn('bg-amber-600 hover:bg-amber-700') : secondaryBtn}
    >
      {busy === 'reprint' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Printer className="w-5 h-5" />}
      Επανεκτύπωση τελευταίου Ζ
    </button>
  );

  const closeWindowButton = (
    <button type="button" onClick={onClose} className={secondaryBtn}>
      Κλείσιμο παραθύρου
    </button>
  );

  const actions = () => {
    if (closed) {
      // Το ταμείο έκλεισε από εδώ: όχι ξανά Χ/κλείσιμο. Χωρίς χαρτί → αντίγραφο του Ζ.
      if (!closed.printed) {
        return (
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            {closeWindowButton}{access.reprint && reprintButton(true)}
          </div>
        );
      }
      return notice?.kind === 'reprinted'
        ? <div className="flex sm:justify-end">{closeWindowButton}</div>
        : null;
    }
    if (confirmingClose && report) {
      return (
        <div data-testid="register-close-confirm" className="rounded-xl border border-blue-500/40 bg-blue-500/10 p-3 flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-t1">
              Θα κλείσει η περίοδος από {athensDateTime(report.periodStart)} και θα τυπωθεί το Ζ.
            </p>
            {wasteMissing && (
              <p className="text-xs text-amber-800 dark:text-amber-300 mt-1">
                Δεν έχει καταχωρηθεί φύρα για την περίοδο — αν υπάρχει, καταχώρησέ τη από το μενού («Φύρα») πριν το κλείσιμο.
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 sm:flex gap-2 shrink-0">
            <button type="button" onClick={() => setConfirmingClose(false)} className={secondaryBtn}>Άκυρο</button>
            <button
              type="button"
              data-testid="register-close-confirm-button"
              onClick={confirmClose}
              disabled={busy !== null}
              className={primaryBtn('bg-blue-600 hover:bg-blue-700')}
            >
              <Lock className="w-5 h-5" />
              Κλείσιμο ταμείου
            </button>
          </div>
        </div>
      );
    }
    if (!access.reprint && !access.x && !access.close) return null;
    return (
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2">
        <p className="flex-1 text-xs text-t3 dark:text-t4">
          {access.x ? 'Η Χ είναι ενδιάμεση αναφορά — δεν κλείνει το ταμείο.' : ''}
        </p>
        {access.reprint && reprintButton(false)}
        {access.x && (
          <button
            type="button"
            onClick={printX}
            disabled={busy !== null || loading || !report}
            className={primaryBtn('bg-amber-600 hover:bg-amber-700')}
          >
            {busy === 'x' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Printer className="w-5 h-5" />}
            Εκτύπωση Χ
          </button>
        )}
        {access.close && (
          <button
            type="button"
            data-testid="register-close"
            onPointerDown={startTrainingHold}
            onPointerUp={endTrainingHold}
            onPointerLeave={clearTrainingHold}
            onPointerCancel={clearTrainingHold}
            onContextMenu={event => event.preventDefault()}
            onClick={() => {
              if (suppressCloseClick.current) {
                suppressCloseClick.current = false;
                return;
              }
              startClose();
            }}
            disabled={busy !== null || loading || !report}
            className={primaryBtn(trainingCloseArmed ? 'bg-orange-500 hover:bg-orange-600' : 'bg-blue-600 hover:bg-blue-700')}
          >
            {busy === 'close' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-5 h-5" />}
            Κλείσιμο ταμείου
          </button>
        )}
      </div>
    );
  };

  const statusNode = status();
  const actionsNode = actions();
  const tabLabel = TAB_META[activeTab].label;

  return (
    <div
      className="cashier-overlay fixed inset-0 z-[70] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Ταμείο"
        className="cashier-dialog bg-surface-1 w-full h-full sm:w-[96%] sm:h-[94%] sm:rounded-2xl overflow-hidden shadow-2xl flex flex-col"
        style={dialogCaps()}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="cashier-dialog-header flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-line bg-surface-1 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-600/20 shrink-0">
              <Calculator className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-t1">Ταμείο</h2>
              {report && (
                <p data-testid="register-period" className="text-xs text-t3 truncate">
                  από {athensDateTime(report.periodStart)} έως {athensDateTime(report.generatedAt)}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={refresh}
              disabled={loading}
              aria-label="Ανανέωση"
              className="w-11 h-11 rounded-xl bg-surface-2 hover:bg-surface-3 flex items-center justify-center active:scale-95 transition-all"
            >
              <RefreshCw className={`w-5 h-5 text-t2 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Κλείσιμο"
              className="w-11 h-11 rounded-xl bg-surface-2 hover:bg-surface-3 flex items-center justify-center active:scale-95 transition-all"
            >
              <X className="w-5 h-5 text-t2" />
            </button>
          </div>
        </div>

        {/* Καρτέλες — μεγάλοι στόχοι αφής· με μία μόνο καρτέλα δεν χρειάζονται. */}
        {access.tabs.length > 1 && (
          <div role="tablist" aria-label="Ταμείο" className="flex shrink-0 gap-1 overflow-x-auto border-b border-line px-2 sm:px-4">
            {access.tabs.map(id => {
              const { label, icon: Icon } = TAB_META[id];
              const selected = id === activeTab;
              return (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  id={`register-tab-${id}`}
                  aria-selected={selected}
                  aria-controls="register-tabpanel"
                  onClick={() => setTab(id)}
                  className={`flex min-h-[52px] shrink-0 items-center gap-2 border-b-[3px] px-4 sm:px-6 text-base font-semibold whitespace-nowrap transition-colors touch-manipulation ${selected
                    ? 'border-blue-600 text-blue-700 dark:border-blue-400 dark:text-blue-300'
                    : 'border-transparent text-t3 hover:text-t1'}`}
                >
                  <Icon className="w-5 h-5 shrink-0" />
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {/* Body — κυλά μόνο αυτό */}
        <div
          role="tabpanel"
          id="register-tabpanel"
          {...(access.tabs.length > 1 ? { 'aria-labelledby': `register-tab-${activeTab}` } : { 'aria-label': tabLabel })}
          // «Προσωπικό»: χωρίς κάθετο padding — η κολλητή εργαλειοθήκη/σύνολα του StaffPanel κολλάνε στην άκρη
          // της κύλισης· με padding άφηναν λωρίδα όπου φαινόταν το περιεχόμενο να περνά από κάτω τους.
          className={`flex-1 min-h-0 overflow-y-auto bg-surface-1 ${activeTab === 'staff' ? 'px-3 sm:px-5' : 'p-3 sm:p-5'}`}
        >
          {body()}
        </div>

        {/* Footer — πάντα ορατό */}
        {(statusNode || actionsNode) && (
          <div data-testid="register-footer" className="shrink-0 border-t border-line px-3 sm:px-5 py-3 space-y-2">
            {statusNode}
            {actionsNode}
          </div>
        )}
      </div>
    </div>
  );
};

const RegisterHubModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { store, employee } = useAuth();
  if (!isOpen || !store?.id) return null;
  const access = registerAccess(store, employee);
  if (!access.any) return null;
  // Νέο στιγμιότυπο σε κάθε άνοιγμα: καθαρή κατάσταση, πρώτη διαθέσιμη καρτέλα, φρέσκια αναφορά.
  return <RegisterHub onClose={onClose} storeId={store.id} access={access} />;
};

export default RegisterHubModal;
